/**
 * API Service for Notification Event Definitions.
 * Event definitions are merchant-scoped.
 *
 * Backend: Alaric.Services.Messaging - EventDefinitionsController
 * Base route: /api/v1/event-definitions
 */

import { Result } from "../../../models/Result";
import {
    EventDefinitionDto,
    CreateEventDefinitionRequest,
    UpdateEventDefinitionRequest,
    NotificationChannel,
    parseAllowedChannels,
} from "../../../models/notifications";
import { messagingHttpService } from "./MessagingHttpService";

const BASE_PATH = "/api/v1/event-definitions";

class EventDefinitionsApiService {
    /**
     * Get all event definitions for a merchant.
     * @param merchantId The merchant ID.
     * @param includeInactive If true, includes inactive event definitions.
     */
    getAll = async (merchantId: number, includeInactive: boolean = false): Promise<Result<EventDefinitionDto[]>> => {
        const params = new URLSearchParams();
        params.append("merchantId", merchantId.toString());
        if (includeInactive) {
            params.append("includeInactive", "true");
        }
        return messagingHttpService.get<EventDefinitionDto[]>(`${BASE_PATH}?${params.toString()}`);
    };

    /**
     * Get only active event definitions for a merchant.
     * @param merchantId The merchant ID.
     */
    getActive = async (merchantId: number): Promise<Result<EventDefinitionDto[]>> => {
        return this.getAll(merchantId, false);
    };

    /**
     * Get a specific event definition by event code.
     * @param merchantId The merchant ID.
     * @param eventCode The unique event code (e.g., "PAYMENT_RECEIVED").
     */
    getByCode = async (merchantId: number, eventCode: string): Promise<Result<EventDefinitionDto>> => {
        return messagingHttpService.get<EventDefinitionDto>(
            `${BASE_PATH}/${encodeURIComponent(eventCode)}?merchantId=${merchantId}`
        );
    };

    /**
     * Get allowed channels for an event as array of NotificationChannel enums.
     * @param merchantId The merchant ID.
     * @param eventCode The event code.
     */
    getAllowedChannels = async (merchantId: number, eventCode: string): Promise<Result<NotificationChannel[]>> => {
        const result = await this.getByCode(merchantId, eventCode);
        if (!result.success) {
            return {
                success: false,
                data: [],
                message: result.message,
            };
        }
        return {
            success: true,
            data: parseAllowedChannels(result.data.allowedChannels),
            message: "",
        };
    };

    /**
     * Get allowed variables for an event.
     * @param merchantId The merchant ID.
     * @param eventCode The event code.
     */
    getAllowedVariables = async (merchantId: number, eventCode: string): Promise<Result<string[]>> => {
        const result = await this.getByCode(merchantId, eventCode);
        if (!result.success) {
            return {
                success: false,
                data: [],
                message: result.message,
            };
        }
        return {
            success: true,
            data: result.data.allowedVariables ?? [],
            message: "",
        };
    };

    /**
     * Create a new event definition.
     * @param request The event definition to create (includes merchantId).
     */
    create = async (request: CreateEventDefinitionRequest): Promise<Result<{ id: string; eventCode: string }>> => {
        return messagingHttpService.post<{ id: string; eventCode: string }>(BASE_PATH, request);
    };

    /**
     * Update an existing event definition.
     * @param merchantId The merchant ID.
     * @param eventCode The event code to update.
     * @param request The updated event definition data.
     */
    update = async (merchantId: number, eventCode: string, request: UpdateEventDefinitionRequest): Promise<Result<void>> => {
        return messagingHttpService.put<void>(
            `${BASE_PATH}/${encodeURIComponent(eventCode)}?merchantId=${merchantId}`,
            request
        );
    };

    /**
     * Activate an event definition.
     * @param merchantId The merchant ID.
     * @param eventCode The event code to activate.
     */
    activate = async (merchantId: number, eventCode: string): Promise<Result<void>> => {
        return this.update(merchantId, eventCode, { isActive: true });
    };

    /**
     * Deactivate an event definition.
     * @param merchantId The merchant ID.
     * @param eventCode The event code to deactivate.
     */
    deactivate = async (merchantId: number, eventCode: string): Promise<Result<void>> => {
        return this.update(merchantId, eventCode, { isActive: false });
    };

    /**
     * Delete an event definition.
     * Note: Cannot delete if templates are using this event code.
     * @param merchantId The merchant ID.
     * @param eventCode The event code to delete.
     */
    delete = async (merchantId: number, eventCode: string): Promise<Result<void>> => {
        return messagingHttpService.delete<void>(
            `${BASE_PATH}/${encodeURIComponent(eventCode)}?merchantId=${merchantId}`
        );
    };

    /**
     * Check if an event code exists for a merchant.
     * @param merchantId The merchant ID.
     * @param eventCode The event code to check.
     */
    exists = async (merchantId: number, eventCode: string): Promise<boolean> => {
        const result = await this.getByCode(merchantId, eventCode);
        return result.success;
    };
}

const eventDefinitionsApiService = new EventDefinitionsApiService();

export { eventDefinitionsApiService };
